import {
  CardMedia,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  TypographyProps,
  styled,
} from "@mui/material";
import Paper from "common/components/Paper";
import React, { useContext } from "react";
import { DraftContext } from "../context";
import { DraftCharacter } from "types/Draft/type";
import CharacterBadges from "../CharacterBadges";
import { RoomContext } from "App/context";
import { Balance, Close, DoNotDisturb } from "@mui/icons-material";
import { Joker } from "assets/icons";
import { BannedCharacter } from "common/components/DraftCharacterCard";
import { API_URL } from "service/root/rootApi";
import { GameMode } from "types/Room/type";

const BackgroundCard = styled(CardMedia)(() => ({
  backgroundImage: `url(/background-gacha.jpg)`,
  position: "relative",
  borderRadius: "1rem",
}));
export const StyledTypography = styled(
  ({
    player,
    ...props
  }: {
    player: "player1" | "player2";
  } & TypographyProps) => (
    <Typography
      color={`common.${player}.text`}
      sx={{
        left: player === "player2" ? "0.5rem" : "unset",
        right: player === "player1" ? "0.5rem" : "unset",
        bgcolor: ({ palette }) => palette.background.default,
        width: "fit-content",
      }}
      {...props}
    />
  ),
)(() => ({
  position: "absolute",
  bottom: 0,
  fontSize: "1rem",
  right: "0.5rem",
  padding: "0 0.2rem",
  borderRadius: "0.5rem",
}));
const getImage = (characterName: string, konami: boolean) => {
  if (konami) {
    try {
      return `${API_URL}assets/genshin/characters/${characterName}/cosplay.gif`;
    } catch (e) {}
  }
  return `${API_URL}assets/genshin/characters/${characterName}/splash.png`;
};
const CardMediaStyled = styled(
  ({
    characterName,
    konami,
    ...props
  }: {
    characterName: string;
    konami: boolean;
  }) => (
    <CardMedia
      component="img"
      src={getImage(characterName, konami)}
      {...props}
    />
  ),
)(() => ({
  objectPosition: "85% 15%",
  borderRadius: "0.5rem",
  transform: "scale(2)",
  position: "relative",
  top: "1rem",
}));
const PaperStyled = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.dark,
  padding: "0",
  overflow: "hidden",
}));
const PulsingPaper = styled(PaperStyled)(({ theme }) => ({
  "@keyframes pulse-pick": {
    "0%, 100%": {
      backgroundColor: "transparent",
    },
    "50%": {
      backgroundColor: theme.palette.common.grey.dark,
    },
  },
  animation: "pulse-pick 2s infinite",
}));

type PicksListType = {
  player: "player1" | "player2";
  playerName: string;
  teamMateNames: Array<string | undefined>;
  picks: DraftCharacter[];
  balancingBans?: DraftCharacter[];
  jokerBans?: DraftCharacter[];
  selected?: DraftCharacter[];
  onClick?: (character: DraftCharacter) => void;
};

const PicksList = (props: PicksListType) => {
  const { params, konami } = useContext(RoomContext);
  const { currentStep } = useContext(DraftContext);
  const {
    player,
    playerName,
    teamMateNames,
    picks,
    selected,
    balancingBans,
    jokerBans,
    onClick,
  } = props;
  const isAbyssMode = params.gameMode === GameMode.SpiralAbyss;
  const isCurrentPick =
    currentStep?.action === "Pick" &&
    (currentStep.player === "Both" ||
      currentStep.player === (player === "player1" ? "Player 1" : "Player 2"));
  const paperHeight = isAbyssMode ? "11.5vh" : "7.5vh";

  const getCharacterCard = (index: number) => {
    if (picks[index] !== undefined) {
      return (
        <BackgroundCard
          key={index}
          sx={{
            overflow: "hidden",
          }}
        >
          <PaperStyled
            sx={{
              height: paperHeight,
              backgroundColor: "transparent",
            }}
          >
            <CharacterBadges character={picks[index]} side={player} hideLevel />
            <CardMediaStyled
              characterName={picks[index].name}
              konami={konami}
            />
          </PaperStyled>
        </BackgroundCard>
      );
    } else if (
      isCurrentPick &&
      selected !== undefined &&
      index >= picks.length &&
      index - picks.length < (currentStep?.quantity ?? 0)
    ) {
      const character = selected[index - picks.length];
      return (
        <BackgroundCard key={index}>
          <PulsingPaper
            sx={{
              height: paperHeight,
            }}
          >
            {character !== undefined ? (
              <>
                <Stack
                  sx={{
                    height: "100%",
                    width: "100%",
                    borderRadius: "12px",
                    position: "absolute",
                    overflow: "hidden",
                  }}
                >
                  <CharacterBadges character={character} side={player} />
                </Stack>
                <CardMediaStyled
                  characterName={character.name}
                  konami={konami}
                />
                {onClick && (
                  <Stack
                    alignItems="center"
                    sx={{
                      position: "absolute",
                      top: "0px",
                      right: player === "player1" ? "-12px" : "",
                      left: player === "player2" ? "-12px" : "",
                      height: "100%",
                    }}
                  >
                    <IconButton
                      onClick={() => onClick(character)}
                      sx={{
                        padding: "2px",
                        margin: "auto",
                        bgcolor: ({ palette }) =>
                          palette.common.player2.banCard,
                        "&:hover": {
                          bgcolor: ({ palette }) =>
                            palette.common.player2.paper,
                        },
                      }}
                    >
                      <Close />
                    </IconButton>
                  </Stack>
                )}
              </>
            ) : (
              ""
            )}
          </PulsingPaper>
        </BackgroundCard>
      );
    } else {
      return (
        <PaperStyled
          key={index}
          sx={{
            height: paperHeight,
          }}
        />
      );
    }
  };

  const getName = () => {
    const mates = teamMateNames.filter((s) => s !== undefined);
    if (mates.length > 0) return `${playerName} & ${mates.join(" & ")}`;
    else return playerName;
  };

  return (
    <Paper
      sx={{
        width: "20rem",
        padding: "0.5rem",
      }}
    >
      <Stack
        direction={player === "player1" ? "row" : "row-reverse"}
        justifyContent="space-between"
        alignItems="center"
      >
        <Tooltip title={getName()}>
          <Typography
            textOverflow="ellipsis"
            overflow="hidden"
            whiteSpace="nowrap"
            color={`common.${player}.text`}
          >
            {getName()}
          </Typography>
        </Tooltip>
        {(balancingBans || jokerBans) && (
          <Tooltip
            arrow
            title={
              <React.Fragment>
                <Stack direction="column" gap={1}>
                  {balancingBans && (
                    <>
                      <Balance />
                      <Stack
                        display="grid"
                        gridTemplateColumns="repeat(2, minmax(70px, 1fr))"
                        gap={1}
                      >
                        {balancingBans.map((char) => (
                          <BannedCharacter
                            key={char.name}
                            character={char}
                            size="small"
                          />
                        ))}
                      </Stack>
                    </>
                  )}
                  {balancingBans && jokerBans && (
                    <Divider orientation="horizontal" flexItem />
                  )}
                  {jokerBans && (
                    <>
                      <Joker />
                      <Stack
                        display="grid"
                        gridTemplateColumns="repeat(2, minmax(70px, 1fr))"
                        gap={1}
                      >
                        {jokerBans.map((char) => (
                          <BannedCharacter
                            key={char.name}
                            character={char}
                            size="small"
                          />
                        ))}
                      </Stack>
                    </>
                  )}
                </Stack>
              </React.Fragment>
            }
          >
            <DoNotDisturb />
          </Tooltip>
        )}
      </Stack>
      <Stack
        direction="column"
        flexGrow={1}
        height="calc(100% - 20px)"
        justifyContent="space-between"
        sx={{ svg: { width: "100%" } }}
      >
        {[...Array(isAbyssMode ? 9 : 13).keys()].map((value) => {
          if (isAbyssMode ? value === 4 : value === 6)
            return <Divider key={"-1"} orientation="horizontal" />;
          else
            return getCharacterCard(
              (isAbyssMode && value > 4) || (!isAbyssMode && value > 6)
                ? value - 1
                : value,
            );
        })}
      </Stack>
    </Paper>
  );
};

export default PicksList;
